import { cva } from 'class-variance-authority';
import * as React from 'react';
import { Link, matchPath } from 'react-router-dom';
import Avatar from 'src/components/ui/avatar';
import { Chip } from 'src/components/ui/chip';
import { Icon } from 'src/components/ui/icon';
import { Tooltip } from 'src/components/ui/tooltip';
import { Typography } from 'src/components/ui/typography';
import { useCurrentPathname } from 'src/layouts/SidebarV2/hooks/useCurrentPathname';
import type { SidebarIcon } from 'src/layouts/SidebarV2/icons';
import type { NavAvatar, NavBadge } from 'src/layouts/SidebarV2/types';
import { cn } from 'src/utils/utils';

const navItemVariants = cva(
  'tw:flex tw:items-center tw:gap-s tw:h-2-5xl tw:max-h-2-5xl tw:rounded-full tw:transition-colors tw:cursor-pointer tw:no-underline tw:border-0 tw:bg-transparent tw:text-left',
  {
    variants: {
      type: {
        default: 'tw:w-full tw:px-m tw:py-s',
        iconOnly: 'tw:w-2-5xl tw:h-2-5xl tw:p-s tw:shrink-0 tw:justify-center'
      },
      state: {
        default:
          'tw:text-text-secondary tw:hover:bg-secondary-hover tw:hover:text-text-primary',
        selected: 'tw:bg-secondary-hover tw:text-text-primary',
        disabled: 'tw:text-text-disabled tw:pointer-events-none'
      }
    },
    defaultVariants: { type: 'default', state: 'default' }
  }
);

export interface NavItemProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onClick'> {
  name: string;
  icon?: SidebarIcon;
  avatar?: NavAvatar;
  badge?: NavBadge;
  /** Hover-revealed trailing controls (each its own button). */
  actionCell?: React.ReactNode;
  trailing?: React.ReactNode;
  /** Always-visible trailing icon (e.g. pulse dot). */
  endIcon?: SidebarIcon;
  endIconColorClass?: string;
  /** When set, the row renders as a <Link>, active when it matches `useCurrentPathname`. */
  to?: string;
  /** Exact-match the route instead of matching any sub-path (default true). */
  end?: boolean;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  /** Icon-only + label shown in a right-side tooltip. */
  collapsed?: boolean;
  /** Icon-only without the tooltip (implied by `collapsed`). */
  iconOnly?: boolean;
  disabled?: boolean;
  /** Explicit selected override for button-mode rows with no `to` to match against. */
  selected?: boolean;
}

const iconColorClass =
  'tw:text-icon-primary tw:group-hover:text-icon-primary-hover';

const NavItem = React.forwardRef<HTMLElement, NavItemProps>(
  (
    {
      name,
      icon,
      avatar,
      badge,
      actionCell,
      trailing,
      endIcon,
      endIconColorClass,
      to,
      end = true,
      onClick,
      collapsed = false,
      iconOnly = false,
      disabled = false,
      selected = false,
      className,
      ...rest
    },
    ref
  ) => {
    const isIconOnly = collapsed || iconOnly;
    const type = isIconOnly ? 'iconOnly' : 'default';
    const resolvedState = disabled
      ? 'disabled'
      : selected
      ? 'selected'
      : 'default';
    const currentPathname = useCurrentPathname();
    const isActive = !!to && !!matchPath({ path: to, end }, currentPathname);

    const leading = avatar ? (
      <div className="tw:relative tw:w-l tw:h-l tw:shrink-0">
        <div className="tw:absolute tw:left-1/2 tw:top-1/2 tw:-translate-x-1/2 tw:-translate-y-1/2 tw:h-xl">
          <Avatar
            type={avatar.src ? 'image' : 'text'}
            src={avatar.src}
            text={avatar.text}
            size={avatar.size}
            variant={avatar.variant}
            colorScheme={avatar.colorScheme}
            className="tw:border-0"
          />
        </div>
      </div>
    ) : icon ? (
      <Icon
        icon={icon}
        size={20}
        colorClass={iconColorClass}
        className="tw:shrink-0"
      />
    ) : null;

    const label = !isIconOnly ? (
      <Typography
        variant="b2"
        align="left"
        className="tw:flex-1 tw:min-w-0 tw:max-w-[150px] tw:truncate tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap"
      >
        {name}
      </Typography>
    ) : null;

    const badgeNode =
      badge && !isIconOnly ? (
        <Chip
          type="badge"
          variant="filled"
          colorScheme={badge.variant === 'Beta' ? 'brand' : 'default'}
          size="small"
          text={
            badge.variant === 'dot' ? undefined : badge.label ?? badge.variant
          }
          className="tw:shrink-0"
        />
      ) : null;

    const trailingNode =
      trailing && !isIconOnly ? (
        <span
          className={cn(
            'tw:ml-auto tw:flex tw:shrink-0 tw:items-center',
            actionCell && 'tw:group-hover:hidden'
          )}
        >
          {trailing}
        </span>
      ) : null;

    const actionNode =
      actionCell && !isIconOnly ? (
        <span
          onClick={(e) => e.stopPropagation()}
          className="tw:ml-auto tw:hidden tw:shrink-0 tw:items-center tw:gap-s tw:group-hover:flex"
        >
          {actionCell}
        </span>
      ) : null;

    const endIconNode =
      endIcon && !isIconOnly ? (
        <Icon
          icon={endIcon}
          sizeClass="tw:h-[12px] tw:w-[12px]"
          colorClass={endIconColorClass ?? 'tw:text-icon-secondary'}
          className="tw:shrink-0 tw:ml-auto"
        />
      ) : null;

    const content = (
      <>
        {leading}
        {label}
        {badgeNode}
        {trailingNode}
        {endIconNode}
        {actionNode}
      </>
    );

    const staticClass = cn(
      navItemVariants({ type, state: resolvedState }),
      'tw:group',
      className
    );

    const activeClass = cn(
      navItemVariants({
        type,
        state: disabled
          ? 'disabled'
          : isActive || selected
          ? 'selected'
          : 'default'
      }),
      'tw:group',
      className
    );

    const row = to ? (
      <Link
        to={to}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={onClick}
        className={activeClass}
        aria-current={isActive ? 'page' : undefined}
        {...rest}
      >
        {content}
      </Link>
    ) : (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={onClick}
        disabled={disabled}
        className={staticClass}
        {...rest}
      >
        {content}
      </button>
    );

    if (collapsed) {
      return <Tooltip trigger={row} title={name} side="right" />;
    }

    return row;
  }
);

NavItem.displayName = 'NavItem';

export { NavItem, navItemVariants };
